import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, UtensilsCrossed, ChevronLeft, ChevronRight, Plus, Trash2, X, Coffee, Sun, Moon, Apple, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lördag', 'Söndag'];
const MEALS = [
  { id: 'breakfast', label: 'Frukost', icon: Coffee },
  { id: 'lunch', label: 'Lunch', icon: Sun },
  { id: 'dinner', label: 'Middag', icon: Moon },
  { id: 'snacks', label: 'Mellanmål', icon: Apple },
];

const QUICK_MEALS = [
  // Frukost
  { name: 'Havregrynsgröt', category: 'breakfast' },
  { name: 'Smoothie', category: 'breakfast' },
  { name: 'Ägg & toast', category: 'breakfast' },
  { name: 'Yoghurt & granola', category: 'breakfast' },
  { name: 'Overnight oats', category: 'breakfast' },
  // Lunch
  { name: 'Sallad', category: 'lunch' },
  { name: 'Soppa', category: 'lunch' },
  { name: 'Rester', category: 'lunch' },
  { name: 'Macka', category: 'lunch' },
  { name: 'Wraps', category: 'lunch' },
  // Middag
  { name: 'Pasta', category: 'dinner' },
  { name: 'Kyckling & ris', category: 'dinner' },
  { name: 'Fisk & potatis', category: 'dinner' },
  { name: 'Tacos', category: 'dinner' },
  { name: 'Wok', category: 'dinner' },
  { name: 'Pizza', category: 'dinner' },
  { name: 'Köttfärssås', category: 'dinner' },
  { name: 'Soppa', category: 'dinner' },
  // Mellanmål
  { name: 'Frukt', category: 'snacks' },
  { name: 'Nötter', category: 'snacks' },
  { name: 'Yoghurt', category: 'snacks' },
  { name: 'Proteinbar', category: 'snacks' },
];

export default function MealPlannerPage() {
  const navigate = useNavigate();
  const { hasAccess, getAuthHeader } = useAuth();
  const [currentWeek, setCurrentWeek] = useState(0); // 0 = this week
  const [mealPlan, setMealPlan] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(null); // { day, meal }
  const [customMeal, setCustomMeal] = useState('');

  useEffect(() => {
    if (hasAccess) {
      loadMealPlan();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const loadMealPlan = async () => {
    try {
      const response = await axios.get(`${API}/meal-plan`, {
        headers: getAuthHeader()
      });
      setMealPlan(response.data.meals || {});
    } catch (error) {
      console.error('Failed to load meal plan:', error);
      // Fallback to localStorage if API fails
      const saved = localStorage.getItem('meal-plan');
      if (saved) {
        setMealPlan(JSON.parse(saved));
      }
    } finally {
      setLoading(false);
    }
  };

  const saveMealPlan = async (updated) => {
    setMealPlan(updated);
    setSaving(true);
    try {
      await axios.put(`${API}/meal-plan`, { meals: updated }, {
        headers: getAuthHeader()
      });
      // Also save to localStorage as backup
      localStorage.setItem('meal-plan', JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save meal plan:', error);
      // Save to localStorage as fallback
      localStorage.setItem('meal-plan', JSON.stringify(updated));
    } finally {
      setSaving(false);
    }
  };

  const getWeekKey = () => {
    const today = new Date();
    today.setDate(today.getDate() + (currentWeek * 7));
    const monday = new Date(today);
    monday.setDate(monday.getDate() - monday.getDay() + 1);
    return monday.toISOString().split('T')[0];
  };

  const getWeekDates = () => {
    const today = new Date();
    today.setDate(today.getDate() + (currentWeek * 7));
    const monday = new Date(today);
    monday.setDate(monday.getDate() - monday.getDay() + 1);
    
    return DAYS.map((day, i) => {
      const date = new Date(monday);
      date.setDate(date.getDate() + i);
      return {
        day,
        date: date.getDate(),
        month: date.toLocaleDateString('sv-SE', { month: 'short' }),
        isToday: date.toDateString() === new Date().toDateString(),
        key: `${getWeekKey()}-${i}`
      };
    });
  };

  const addMeal = (dayKey, mealType, mealName) => {
    const weekKey = getWeekKey();
    const key = `${weekKey}-${dayKey}`;
    const current = mealPlan[key] || {};
    const currentMeals = current[mealType] || [];
    
    const updated = {
      ...mealPlan,
      [key]: {
        ...current,
        [mealType]: [...currentMeals, mealName]
      }
    };
    saveMealPlan(updated);
    setShowAddModal(null);
    setCustomMeal('');
    toast.success('Måltid tillagd!');
  };

  const removeMeal = (dayKey, mealType, index) => {
    const weekKey = getWeekKey();
    const key = `${weekKey}-${dayKey}`;
    const current = mealPlan[key] || {};
    const currentMeals = [...(current[mealType] || [])];
    currentMeals.splice(index, 1);
    
    const updated = {
      ...mealPlan,
      [key]: {
        ...current,
        [mealType]: currentMeals
      }
    };
    saveMealPlan(updated);
  };

  const getMealsForDay = (dayIndex, mealType) => {
    const weekKey = getWeekKey();
    const key = `${weekKey}-${dayIndex}`;
    return mealPlan[key]?.[mealType] || [];
  };

  const weekDates = getWeekDates();

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="meal-planner-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Måltidsplanering</h1>
          </div>
        </header>
        <main className="max-w-6xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till måltidsplanering.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="meal-planner-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Måltidsplanering</h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-4">
        {/* Week navigation */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setCurrentWeek(currentWeek - 1)} className="rounded-full">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <h2 className="font-semibold">
              {currentWeek === 0 ? 'Denna vecka' : currentWeek === 1 ? 'Nästa vecka' : currentWeek === -1 ? 'Förra veckan' : `Vecka ${currentWeek > 0 ? '+' : ''}${currentWeek}`}
            </h2>
            <p className="text-sm text-muted-foreground">
              {weekDates[0].date} {weekDates[0].month} - {weekDates[6].date} {weekDates[6].month}
            </p>
          </div>
          <Button variant="ghost" onClick={() => setCurrentWeek(currentWeek + 1)} className="rounded-full">
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-emerald-200 border-t-emerald-500 rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="grid grid-cols-8 gap-2 min-w-[800px]">
              {/* Header row */}
              <div className="p-2"></div>
              {weekDates.map((d, i) => (
                <div 
                  key={i} 
                  className={`p-2 text-center rounded-lg ${d.isToday ? 'bg-emerald-100' : ''}`}
                >
                  <p className="font-medium text-sm">{d.day}</p>
                  <p className={`text-xs ${d.isToday ? 'text-emerald-600 font-semibold' : 'text-muted-foreground'}`}>
                    {d.date} {d.month}
                  </p>
                </div>
              ))}

              {/* Meal rows */}
              {MEALS.map((meal) => (
                <>
                  <div key={meal.id} className="p-2 flex items-center gap-2">
                    <meal.icon className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">{meal.label}</span>
                  </div>
                  {weekDates.map((d, dayIndex) => {
                    const meals = getMealsForDay(dayIndex, meal.id);
                    return (
                      <div 
                        key={`${meal.id}-${dayIndex}`}
                        className={`p-2 bg-white rounded-lg border border-border/30 min-h-[80px] ${d.isToday ? 'border-emerald-200' : ''}`}
                      >
                        <div className="space-y-1">
                          {meals.map((m, idx) => (
                            <div key={idx} className="flex items-center justify-between group">
                              <span className="text-xs truncate flex-1">{m}</span>
                              <button 
                                onClick={() => removeMeal(dayIndex, meal.id, idx)}
                                className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 p-0.5"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => setShowAddModal({ day: dayIndex, meal: meal.id, dayName: d.day })}
                          className="w-full mt-1 text-xs text-muted-foreground hover:text-emerald-600 flex items-center justify-center gap-1 py-1"
                        >
                          <Plus className="w-3 h-3" />
                          <span>Lägg till</span>
                        </button>
                      </div>
                    );
                  })}
                </>
              ))}
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-100">
          <p className="text-emerald-800 text-sm">
            💡 <strong>Tips:</strong> Planera veckans måltider på söndagen för att spara tid och pengar under veckan!
          </p>
        </div>
      </main>

      {/* Add meal modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">
                Lägg till {MEALS.find(m => m.id === showAddModal.meal)?.label.toLowerCase()} - {showAddModal.dayName}
              </h3>
              <button onClick={() => setShowAddModal(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Quick add */}
            <div className="mb-4">
              <p className="text-sm text-muted-foreground mb-2">Snabbval</p>
              <div className="flex flex-wrap gap-2">
                {QUICK_MEALS.filter(m => m.category === showAddModal.meal || showAddModal.meal === 'snacks').slice(0, 10).map((m, i) => (
                  <button
                    key={i}
                    onClick={() => addMeal(showAddModal.day, showAddModal.meal, m.name)}
                    className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm hover:bg-emerald-100"
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom meal */}
            <div>
              <p className="text-sm text-muted-foreground mb-2">Eller skriv eget</p>
              <div className="flex gap-2">
                <Input
                  value={customMeal}
                  onChange={(e) => setCustomMeal(e.target.value)}
                  placeholder="T.ex. Lasagne"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && customMeal.trim()) {
                      addMeal(showAddModal.day, showAddModal.meal, customMeal.trim());
                    }
                  }}
                />
                <Button 
                  onClick={() => {
                    if (customMeal.trim()) {
                      addMeal(showAddModal.day, showAddModal.meal, customMeal.trim());
                    }
                  }}
                  className="rounded-full"
                >
                  Lägg till
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
